import React, { useCallback, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  evaluateNumericExpression,
  formatEvaluatedNumber,
  formatNumericInputValue,
  hasEquivalentNumericValue,
} from '@/lib/numericExpressions';

interface ExpressionNumberInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  value: number | string | null | undefined;
  onValueChange: (value: number | string | null) => void;
  emptyValue?: string | null;
}

export const ExpressionNumberInput = React.forwardRef<HTMLInputElement, ExpressionNumberInputProps>(
  (
    {
      value,
      onValueChange,
      emptyValue = '',
      className,
      inputMode = 'text',
      onBlur,
      onFocus,
      ...props
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(() => formatNumericInputValue(value));
    const [isFocused, setIsFocused] = useState(false);
    const lastCommittedDisplayRef = useRef(formatNumericInputValue(value));
    const latestValueRef = useRef<number | string | null | undefined>(value);
    const didEditDuringFocusRef = useRef(false);

    useEffect(() => {
      const nextDisplay = formatNumericInputValue(value);
      latestValueRef.current = value;
      lastCommittedDisplayRef.current = nextDisplay;

      if (!isFocused) {
        setInputValue(nextDisplay);
      }
    }, [isFocused, value]);

    const commitValue = useCallback((rawValue: string, normalizeDisplay: boolean) => {
      const trimmedValue = rawValue.trim();

      if (trimmedValue === '') {
        lastCommittedDisplayRef.current = '';

        if (latestValueRef.current !== emptyValue) {
          onValueChange(emptyValue);
        }

        if (normalizeDisplay) {
          setInputValue('');
        }

        return true;
      }

      const evaluatedValue = evaluateNumericExpression(trimmedValue);
      if (evaluatedValue === null) {
        if (normalizeDisplay) {
          setInputValue(lastCommittedDisplayRef.current);
        }
        return false;
      }

      const formattedValue = formatEvaluatedNumber(evaluatedValue);
      lastCommittedDisplayRef.current = formattedValue;

      if (!hasEquivalentNumericValue(latestValueRef.current, evaluatedValue)) {
        onValueChange(evaluatedValue);
      }

      if (normalizeDisplay) {
        setInputValue(formattedValue);
      }

      return true;
    }, [emptyValue, onValueChange]);

    return (
      <input
        {...props}
        ref={ref}
        type="text"
        inputMode={inputMode}
        value={inputValue}
        onFocus={(event) => {
          setIsFocused(true);
          didEditDuringFocusRef.current = false;
          onFocus?.(event);
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          didEditDuringFocusRef.current = true;
          setInputValue(nextValue);
          commitValue(nextValue, false);
        }}
        onBlur={(event) => {
          setIsFocused(false);

          if (didEditDuringFocusRef.current) {
            commitValue(inputValue, true);
          } else {
            setInputValue(formatNumericInputValue(value));
          }

          onBlur?.(event);
        }}
        className={clsx(className)}
      />
    );
  }
);

ExpressionNumberInput.displayName = 'ExpressionNumberInput';

export default ExpressionNumberInput;
